import { createSignal, onMount, For, Show } from 'solid-js';
import { useActiveBot } from '../../state/activeBot';
import { useBotFanOut } from '../../hooks/useBotFanOut';
import { BOT_ACTIONS } from '../../services/resourceActions';
import ItemStackView from '../../components/ItemStackView';
import { fromIdentifier } from '../../services/itemRefAdapters';
import { useCraftingResolver } from '../../services/craftingResolverContext';
import { useApi, isOk } from '../../api';
import { showConfirm } from '../../components/Dialog';
import { useToast } from '../../components/MessageBox';
import Modal from '../../components/Modal';
import { Skeleton } from '../../components/Skeleton';
import { EmptyState } from '../../components/EmptyState';
import { useInventoryApi, type StorageInfo } from '../../services/inventoryApi';
import { Citybuild } from '../../models/Citybuild';
import Text from '../../components/Text';
import StorageSelect from '../../components/StorageSelect';
import LocationPicker from '../../components/LocationPicker';
import AreaInput from '../../components/AreaInput';
import { useTranslation } from '../../hooks/useTranslation';
import BotScoped from '../../components/BotScoped';
import { formatDateTime } from '../../services/dateFormat';
import { firstError } from '../../api/envelope';
import type {
    CropFieldDto, CropHarvestConfigDto, CropPlantConfigDto, CropPlantConfigInputDto,
} from '../../api/generated/schemas';
import styles from './CropFarmer.module.css';

const citybuildOptions = Object.keys(Citybuild).filter(k => isNaN(Number(k))) as (keyof typeof Citybuild)[];

const HARVEST_MODE_HARVEST_ONLY = 0;
const HARVEST_MODE_REPLANT_EXACT = 1;

type PercentKey = 'wheatPercent' | 'carrotPercent' | 'potatoPercent' | 'melonPercent' | 'pumpkinPercent';
type CountKey = 'wheatCount' | 'carrotCount' | 'potatoCount' | 'melonCount' | 'pumpkinCount' | 'netherWartCount';

const FARMLAND_CROPS: { item: string; label: string; percent: PercentKey }[] = [
    { item: 'wheat', label: 'CROP_FARMER_CROP_WHEAT', percent: 'wheatPercent' },
    { item: 'carrot', label: 'CROP_FARMER_CROP_CARROT', percent: 'carrotPercent' },
    { item: 'potato', label: 'CROP_FARMER_CROP_POTATO', percent: 'potatoPercent' },
    { item: 'melon', label: 'CROP_FARMER_CROP_MELON', percent: 'melonPercent' },
    { item: 'pumpkin', label: 'CROP_FARMER_CROP_PUMPKIN', percent: 'pumpkinPercent' },
];

const CROP_COUNTS: { item: string; label: string; count: CountKey }[] = [
    { item: 'wheat', label: 'CROP_FARMER_CROP_WHEAT', count: 'wheatCount' },
    { item: 'carrot', label: 'CROP_FARMER_CROP_CARROT', count: 'carrotCount' },
    { item: 'potato', label: 'CROP_FARMER_CROP_POTATO', count: 'potatoCount' },
    { item: 'melon', label: 'CROP_FARMER_CROP_MELON', count: 'melonCount' },
    { item: 'pumpkin', label: 'CROP_FARMER_CROP_PUMPKIN', count: 'pumpkinCount' },
    { item: 'nether_wart', label: 'CROP_FARMER_CROP_NETHER_WART', count: 'netherWartCount' },
];

interface FieldForm {
    name: string; citybuild: number; location: string;
    x1: number; y1: number; z1: number; x2: number; y2: number; z2: number;
}

interface HarvestForm {
    id: string | null; fieldId: string; name: string; mode: number;
    unloadStorageId: string; minFreeSlots: number; compress: boolean; isEnabled: boolean;
}

type PlantForm = CropPlantConfigInputDto & { id: string | null };

const emptyFieldForm = (): FieldForm => ({ name: '', citybuild: 0, location: '', x1: 0, y1: 0, z1: 0, x2: 0, y2: 0, z2: 0 });

export default function CropFarmerPage() {
    return <BotScoped groupAware><CropFarmerInner /></BotScoped>;
}

function CropFarmerInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const resolver = useCraftingResolver();
    const inv = useInventoryApi();
    const { botId: activeBotId, targetIds } = useActiveBot();
    const fanOut = useBotFanOut();
    const botId = () => activeBotId() ?? '';

    const [fields, setFields] = createSignal<CropFieldDto[]>([]);
    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [saving, setSaving] = createSignal(false);

    const [showFieldForm, setShowFieldForm] = createSignal(false);
    const [editFieldId, setEditFieldId] = createSignal<string | null>(null);
    const [fieldForm, setFieldForm] = createSignal<FieldForm>(emptyFieldForm());
    const [harvestForm, setHarvestForm] = createSignal<HarvestForm | null>(null);
    const [plantForm, setPlantForm] = createSignal<PlantForm | null>(null);

    onMount(async () => {
        try {
            const [fieldRes, storageList] = await Promise.all([
                api.cropFarmer.getCropFields(),
                inv.fetchStorages(),
            ]);
            if (isOk(fieldRes.data)) setFields(fieldRes.data.value.fields ?? []);
            else toast?.error(firstError(fieldRes.data) ?? t('CROP_FARMER_LOAD_FAILED'));
            setStorages(storageList);
        } catch (e: any) {
            toast?.error(e?.message ?? t('CROP_FARMER_LOAD_FAILED'));
        } finally { setLoading(false); }
    });

    async function loadFields() {
        const res = await api.cropFarmer.getCropFields();
        if (isOk(res.data)) setFields(res.data.value.fields ?? []);
    }

    const patchField = (patch: Partial<FieldForm>) => setFieldForm({ ...fieldForm(), ...patch });
    const patchHarvest = (patch: Partial<HarvestForm>) => setHarvestForm({ ...harvestForm()!, ...patch });
    const patchPlant = (patch: Partial<PlantForm>) => setPlantForm({ ...plantForm()!, ...patch });

    function openCreateField() {
        setEditFieldId(null);
        setFieldForm(emptyFieldForm());
        setShowFieldForm(true);
    }

    function openEditField(field: CropFieldDto) {
        setEditFieldId(field.id);
        setFieldForm({
            name: field.name, citybuild: field.citybuild, location: field.location,
            x1: field.x1, y1: field.y1, z1: field.z1, x2: field.x2, y2: field.y2, z2: field.z2,
        });
        setShowFieldForm(true);
    }

    async function saveField(e: Event) {
        e.preventDefault();
        const id = editFieldId();
        const form = fieldForm();
        const existing = fields().find(f => f.id === id);
        const boxChanged = existing && (
            existing.x1 !== form.x1 || existing.y1 !== form.y1 || existing.z1 !== form.z1
            || existing.x2 !== form.x2 || existing.y2 !== form.y2 || existing.z2 !== form.z2);
        if (boxChanged && !(await showConfirm(t('CROP_FARMER_BOX_CHANGED_CONFIRM'), true))) return;

        setSaving(true);
        try {
            const body = { ...form, name: form.name.trim() || t('CROP_FARMER_DEFAULT_FIELD_NAME'), location: form.location.trim() };
            const res = id
                ? await api.cropFarmer.updateCropField(id, body)
                : await api.cropFarmer.createCropField(body);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('CROP_FARMER_SAVE_FAILED'));
                return;
            }
            setShowFieldForm(false);
            toast?.info(id ? t('CROP_FARMER_FIELD_UPDATED') : t('CROP_FARMER_FIELD_CREATED'));
            await loadFields();
        } finally { setSaving(false); }
    }

    async function deleteField(field: CropFieldDto) {
        if (!(await showConfirm(t('CROP_FARMER_FIELD_DELETE_CONFIRM', field.name), true))) return;
        const res = await api.cropFarmer.deleteCropField(field.id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('CROP_FARMER_DELETE_FAILED'));
            return;
        }
        toast?.info(t('CROP_FARMER_FIELD_DELETED'));
        await loadFields();
    }

    async function scanField(field: CropFieldDto) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('CROP_FARMER_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.cropFarmer.scanCropField(field.id, { botId: targetBotId }),
            { success: t('CROP_FARMER_SCAN_STARTED'), failure: t('CROP_FARMER_START_FAILED') });
    }

    function openHarvest(fieldId: string, config?: CropHarvestConfigDto) {
        setHarvestForm(config
            ? { ...config }
            : {
                id: null, fieldId, name: '', mode: HARVEST_MODE_REPLANT_EXACT,
                unloadStorageId: storages()[0]?.id ?? '', minFreeSlots: 4, compress: false, isEnabled: true,
            });
    }

    function openPlant(fieldId: string, config?: CropPlantConfigDto) {
        setPlantForm(config
            ? { ...config }
            : {
                id: null, fieldId, name: '', sourceStorageId: storages()[0]?.id ?? '',
                wheatPercent: 100, carrotPercent: 0, potatoPercent: 0, melonPercent: 0, pumpkinPercent: 0,
                plantNetherWart: false, isEnabled: true,
            });
    }

    const mixTotal = () => {
        const form = plantForm();
        return form ? FARMLAND_CROPS.reduce((sum, crop) => sum + (form[crop.percent] || 0), 0) : 0;
    };

    async function saveHarvest(e: Event) {
        e.preventDefault();
        const form = harvestForm();
        if (!form) return;
        if (!form.unloadStorageId) { toast?.error(t('CROP_FARMER_SELECT_STORAGE')); return; }
        setSaving(true);
        try {
            const { id, ...rest } = form;
            const body = { ...rest, name: rest.name.trim() || t('CROP_FARMER_DEFAULT_HARVEST_NAME') };
            const res = id
                ? await api.cropFarmer.updateCropHarvestConfig(id, body)
                : await api.cropFarmer.createCropHarvestConfig(body);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('CROP_FARMER_SAVE_FAILED'));
                return;
            }
            setHarvestForm(null);
            toast?.info(t('CROP_FARMER_CONFIG_SAVED'));
            await loadFields();
        } finally { setSaving(false); }
    }

    async function savePlant(e: Event) {
        e.preventDefault();
        const form = plantForm();
        if (!form) return;
        if (!form.sourceStorageId) { toast?.error(t('CROP_FARMER_SELECT_STORAGE')); return; }
        if (mixTotal() !== 100) { toast?.error(t('CROP_FARMER_MIX_NOT_100')); return; }
        setSaving(true);
        try {
            const { id, ...rest } = form;
            const body = { ...rest, name: rest.name.trim() || t('CROP_FARMER_DEFAULT_PLANT_NAME') };
            const res = id
                ? await api.cropFarmer.updateCropPlantConfig(id, body)
                : await api.cropFarmer.createCropPlantConfig(body);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('CROP_FARMER_SAVE_FAILED'));
                return;
            }
            setPlantForm(null);
            toast?.info(t('CROP_FARMER_CONFIG_SAVED'));
            await loadFields();
        } finally { setSaving(false); }
    }

    async function deleteHarvest(config: CropHarvestConfigDto) {
        if (!(await showConfirm(t('CROP_FARMER_CONFIG_DELETE_CONFIRM', config.name), true))) return;
        const res = await api.cropFarmer.deleteCropHarvestConfig(config.id);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('CROP_FARMER_DELETE_FAILED')); return; }
        toast?.info(t('CROP_FARMER_CONFIG_DELETED'));
        await loadFields();
    }

    async function deletePlant(config: CropPlantConfigDto) {
        if (!(await showConfirm(t('CROP_FARMER_CONFIG_DELETE_CONFIRM', config.name), true))) return;
        const res = await api.cropFarmer.deleteCropPlantConfig(config.id);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('CROP_FARMER_DELETE_FAILED')); return; }
        toast?.info(t('CROP_FARMER_CONFIG_DELETED'));
        await loadFields();
    }

    async function runHarvest(config: CropHarvestConfigDto) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('CROP_FARMER_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.cropFarmer.runCropHarvestConfig(config.id, { botId: targetBotId }),
            { success: t('CROP_FARMER_HARVEST_STARTED'), failure: t('CROP_FARMER_START_FAILED') });
    }

    async function runPlant(config: CropPlantConfigDto) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('CROP_FARMER_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.cropFarmer.runCropPlantConfig(config.id, { botId: targetBotId }),
            { success: t('CROP_FARMER_PLANT_STARTED'), failure: t('CROP_FARMER_START_FAILED') });
    }

    async function stop() {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('CROP_FARMER_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.cropFarmer.stopCropFarmer({ botId: targetBotId }),
            { success: t('CROP_FARMER_STOPPED'), failure: t('CROP_FARMER_STOP_FAILED') });
    }

    function storageName(id: string) {
        return storages().find(s => s.id === id)?.name ?? t('CROP_FARMER_UNKNOWN_STORAGE');
    }

    const cropIcon = (item: string) => <ItemStackView stack={fromIdentifier(item, 0, resolver())} size={20} bare />;

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="CROP_FARMER_TITLE"/></h1>
                <div class={styles.headerActions}>
                    <button class="btn btn-secondary" onClick={stop} disabled={!botId()} title={t('CROP_FARMER_STOP_HINT')}>
                        <i class="fa-solid fa-stop" /> <Text val="STOP"/>
                    </button>
                    <button class="btn btn-primary" onClick={openCreateField}>
                        <i class="fa-solid fa-plus" /> <Text val="CROP_FARMER_NEW_FIELD"/>
                    </button>
                </div>
            </div>
            <p class={styles.subtitle}><Text val="CROP_FARMER_SUBTITLE"/></p>

            <Show when={showFieldForm()}>
                <form class={`fcard ${styles.form}`} onSubmit={saveField}>
                    <h2 class={styles.formTitle}>
                        {editFieldId() ? <Text val="CROP_FARMER_EDIT_FIELD"/> : <Text val="CROP_FARMER_CREATE_FIELD"/>}
                    </h2>
                    <div class={styles.formGrid}>
                        <label class={styles.field}>
                            <span class={styles.fieldLabel}><Text val="CROP_FARMER_F_NAME"/></span>
                            <input class={styles.input} value={fieldForm().name} onInput={e => patchField({ name: e.currentTarget.value })} placeholder={t('CROP_FARMER_FIELD_NAME_PLACEHOLDER')} />
                        </label>
                        <label class={styles.field}>
                            <span class={styles.fieldLabel}><Text val="CROP_FARMER_F_CITYBUILD"/></span>
                            <select class={styles.input} onChange={e => patchField({ citybuild: parseInt(e.currentTarget.value) })}>
                                <For each={citybuildOptions}>{(cb) => <option value={Citybuild[cb]} selected={Citybuild[cb] === fieldForm().citybuild}>{cb}</option>}</For>
                            </select>
                        </label>
                        <label class={styles.field}>
                            <span class={styles.fieldLabel}><Text val="CROP_FARMER_F_LOCATION"/></span>
                            <LocationPicker value={fieldForm().location} onChange={location => patchField({ location })} optional />
                        </label>
                    </div>

                    <AreaInput
                        corner1={{ x: fieldForm().x1, y: fieldForm().y1, z: fieldForm().z1 }}
                        corner2={{ x: fieldForm().x2, y: fieldForm().y2, z: fieldForm().z2 }}
                        onChange={(corner1, corner2) => patchField({
                            x1: corner1.x, y1: corner1.y, z1: corner1.z,
                            x2: corner2.x, y2: corner2.y, z2: corner2.z,
                        })}
                        fieldId="cropFarmer.field.area"
                        label={t('CROP_FARMER_F_AREA')}
                        citybuild={fieldForm().citybuild}
                        allowedBots={botId() ? [botId()] : []} />
                    <span class={styles.fieldHint}><Text val="CROP_FARMER_F_CORNER_HINT"/></span>

                    <div class={styles.formActions}>
                        <button type="button" class="btn btn-secondary" onClick={() => setShowFieldForm(false)} disabled={saving()}>
                            <Text val="CANCEL"/>
                        </button>
                        <button type="submit" class="btn btn-primary" disabled={saving()}>
                            <i class={saving() ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-floppy-disk'} /> <Text val="SAVE"/>
                        </button>
                    </div>
                </form>
            </Show>

            <Show when={!loading()} fallback={
                <div class={styles.list}>
                    <Skeleton variant="card" height="14rem" />
                    <Skeleton variant="card" height="14rem" />
                </div>
            }>
                <Show when={fields().length > 0 || showFieldForm()} fallback={
                    <EmptyState icon="fa-wheat-awn" title={t('CROP_FARMER_EMPTY')} hint={t('CROP_FARMER_EMPTY_HINT')}
                        action={<button class="btn btn-primary" onClick={openCreateField}><i class="fa-solid fa-plus" /> <Text val="CROP_FARMER_NEW_FIELD"/></button>} />
                }>
                    <div class={styles.list}>
                        <For each={fields()}>
                            {(field) => (
                                <div class={`fcard ${styles.card}`}>
                                    <div class={styles.cardHeader}>
                                        <div>
                                            <div class={styles.cardName}>{field.name}</div>
                                            <div class={styles.cardMeta}>
                                                {field.citybuildName}{field.location ? ` · ${field.location}` : ''}
                                                {' · '}({field.x1}, {field.y1}, {field.z1}) <Text val="CROP_FARMER_TO"/> ({field.x2}, {field.y2}, {field.z2})
                                            </div>
                                        </div>
                                        <div class={styles.cardActions}>
                                            <button class="btn btn-secondary btn-sm" onClick={() => scanField(field)} disabled={!botId()} title={t('CROP_FARMER_SCAN_HINT')}>
                                                <i class="fa-solid fa-satellite-dish" /> <Text val="CROP_FARMER_SCAN"/>
                                            </button>
                                            <button class="btn btn-secondary btn-sm btn-icon" onClick={() => openEditField(field)} title={t('EDIT')}>
                                                <i class="fa-solid fa-pen" />
                                            </button>
                                            <button class="btn btn-danger btn-sm btn-icon" onClick={() => deleteField(field)} title={t('DELETE')}>
                                                <i class="fa-solid fa-trash" />
                                            </button>
                                        </div>
                                    </div>

                                    <Show when={field.lastScannedAt} fallback={
                                        <div class={styles.notScanned}>
                                            <i class="fa-solid fa-circle-info" /> <Text val="CROP_FARMER_NOT_SCANNED"/>
                                        </div>
                                    }>
                                        <div class={styles.stats}>
                                            <div class={styles.stat}><span class={styles.statValue}>{field.farmlandCount}</span><span class={styles.statLabel}><Text val="CROP_FARMER_STAT_FARMLAND"/></span></div>
                                            <div class={styles.stat}><span class={styles.statValue}>{field.soulSandCount}</span><span class={styles.statLabel}><Text val="CROP_FARMER_STAT_SOUL_SAND"/></span></div>
                                            <div class={styles.stat}><span class={styles.statValue}>{field.emptyCount}</span><span class={styles.statLabel}><Text val="CROP_FARMER_STAT_EMPTY"/></span></div>
                                            <div class={styles.stat}><span class={styles.statValue}>{field.matureCount}</span><span class={styles.statLabel}><Text val="CROP_FARMER_STAT_MATURE"/></span></div>
                                            <Show when={field.revertedCount > 0}>
                                                <div class={`${styles.stat} ${styles.statWarning}`} title={t('CROP_FARMER_STAT_REVERTED_HINT')}>
                                                    <span class={styles.statValue}>{field.revertedCount}</span><span class={styles.statLabel}><Text val="CROP_FARMER_STAT_REVERTED"/></span>
                                                </div>
                                            </Show>
                                        </div>
                                        <div class={styles.cropChips}>
                                            <For each={CROP_COUNTS.filter(crop => field[crop.count] > 0)}>
                                                {(crop) => (
                                                    <span class={styles.cropChip} title={t(crop.label)}>
                                                        {cropIcon(crop.item)} {field[crop.count]}
                                                    </span>
                                                )}
                                            </For>
                                        </div>
                                        <div class={styles.scannedAt}>
                                            {t('CROP_FARMER_LAST_SCAN', formatDateTime(field.lastScannedAt))}
                                        </div>
                                    </Show>

                                    <div class={styles.section}>
                                        <div class={styles.sectionHeader}>
                                            <span class={styles.sectionTitle}><i class="fa-solid fa-scissors" /> <Text val="CROP_FARMER_HARVEST"/></span>
                                            <button class="btn btn-secondary btn-sm" onClick={() => openHarvest(field.id)}>
                                                <i class="fa-solid fa-plus" /> <Text val="CROP_FARMER_ADD_HARVEST"/>
                                            </button>
                                        </div>
                                        <Show when={field.harvestConfigs.length > 0} fallback={<p class={styles.sectionEmpty}><Text val="CROP_FARMER_NO_HARVEST"/></p>}>
                                            <For each={field.harvestConfigs}>
                                                {(config) => (
                                                    <div class={`${styles.configRow} ${!config.isEnabled ? styles.configDisabled : ''}`}>
                                                        <div class={styles.configInfo}>
                                                            <span class={styles.configName}>{config.name}</span>
                                                            <span class={styles.configMeta}>
                                                                {config.mode === HARVEST_MODE_REPLANT_EXACT ? t('CROP_FARMER_MODE_REPLANT') : t('CROP_FARMER_MODE_HARVEST_ONLY')}
                                                                {' · '}<i class="fa-solid fa-warehouse" /> {storageName(config.unloadStorageId)}
                                                            </span>
                                                        </div>
                                                        <div class={styles.cardActions}>
                                                            <button class="btn btn-primary btn-sm" onClick={() => runHarvest(config)} disabled={!botId() || !config.isEnabled}>
                                                                <i class="fa-solid fa-play" /> <Text val="RUN"/>
                                                            </button>
                                                            <button class="btn btn-secondary btn-sm btn-icon" onClick={() => openHarvest(field.id, config)} title={t('EDIT')}>
                                                                <i class="fa-solid fa-pen" />
                                                            </button>
                                                            <button class="btn btn-danger btn-sm btn-icon" onClick={() => deleteHarvest(config)} title={t('DELETE')}>
                                                                <i class="fa-solid fa-trash" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </For>
                                        </Show>
                                    </div>

                                    <div class={styles.section}>
                                        <div class={styles.sectionHeader}>
                                            <span class={styles.sectionTitle}><i class="fa-solid fa-seedling" /> <Text val="CROP_FARMER_PLANT"/></span>
                                            <button class="btn btn-secondary btn-sm" onClick={() => openPlant(field.id)}>
                                                <i class="fa-solid fa-plus" /> <Text val="CROP_FARMER_ADD_PLANT"/>
                                            </button>
                                        </div>
                                        <Show when={field.plantConfigs.length > 0} fallback={<p class={styles.sectionEmpty}><Text val="CROP_FARMER_NO_PLANT"/></p>}>
                                            <For each={field.plantConfigs}>
                                                {(config) => (
                                                    <div class={`${styles.configRow} ${!config.isEnabled ? styles.configDisabled : ''}`}>
                                                        <div class={styles.configInfo}>
                                                            <span class={styles.configName}>{config.name}</span>
                                                            <span class={styles.configMeta}>
                                                                <For each={FARMLAND_CROPS.filter(crop => config[crop.percent] > 0)}>
                                                                    {(crop) => <span class={styles.mixChip}>{cropIcon(crop.item)} {config[crop.percent]}%</span>}
                                                                </For>
                                                                <Show when={config.plantNetherWart}>
                                                                    <span class={styles.mixChip}>{cropIcon('nether_wart')} <Text val="CROP_FARMER_SOUL_SAND_SHORT"/></span>
                                                                </Show>
                                                                <span><i class="fa-solid fa-warehouse" /> {storageName(config.sourceStorageId)}</span>
                                                            </span>
                                                        </div>
                                                        <div class={styles.cardActions}>
                                                            <button class="btn btn-primary btn-sm" onClick={() => runPlant(config)} disabled={!botId() || !config.isEnabled}>
                                                                <i class="fa-solid fa-play" /> <Text val="RUN"/>
                                                            </button>
                                                            <button class="btn btn-secondary btn-sm btn-icon" onClick={() => openPlant(field.id, config)} title={t('EDIT')}>
                                                                <i class="fa-solid fa-pen" />
                                                            </button>
                                                            <button class="btn btn-danger btn-sm btn-icon" onClick={() => deletePlant(config)} title={t('DELETE')}>
                                                                <i class="fa-solid fa-trash" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </For>
                                        </Show>
                                    </div>
                                </div>
                            )}
                        </For>
                    </div>
                </Show>
            </Show>

            <Modal open={harvestForm() !== null} onClose={() => setHarvestForm(null)} busy={saving()}
                title={harvestForm()?.id ? <Text val="CROP_FARMER_EDIT_HARVEST"/> : <Text val="CROP_FARMER_ADD_HARVEST"/>}>
                <Show when={harvestForm()}>
                    {(form) => (
                        <form class={styles.form} onSubmit={saveHarvest}>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="CROP_FARMER_F_NAME"/></span>
                                <input class={styles.input} value={form().name} onInput={e => patchHarvest({ name: e.currentTarget.value })} placeholder={t('CROP_FARMER_DEFAULT_HARVEST_NAME')} />
                            </label>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="CROP_FARMER_F_MODE"/></span>
                                <select class={styles.input} value={form().mode} onChange={e => patchHarvest({ mode: parseInt(e.currentTarget.value) })}>
                                    <option value={HARVEST_MODE_REPLANT_EXACT}>{t('CROP_FARMER_MODE_REPLANT')}</option>
                                    <option value={HARVEST_MODE_HARVEST_ONLY}>{t('CROP_FARMER_MODE_HARVEST_ONLY')}</option>
                                </select>
                                <span class={styles.fieldHint}>
                                    {form().mode === HARVEST_MODE_REPLANT_EXACT ? t('CROP_FARMER_MODE_REPLANT_HINT') : t('CROP_FARMER_MODE_HARVEST_ONLY_HINT')}
                                </span>
                            </label>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="CROP_FARMER_F_UNLOAD_STORAGE"/></span>
                                <StorageSelect storages={storages()} value={form().unloadStorageId} onChange={unloadStorageId => patchHarvest({ unloadStorageId })} placeholder={t('CROP_FARMER_SELECT_STORAGE_OPTION')} />
                            </label>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="CROP_FARMER_F_MINFREE"/></span>
                                <input class={styles.input} type="number" min="1" max="35" value={form().minFreeSlots} onInput={e => patchHarvest({ minFreeSlots: parseInt(e.currentTarget.value) || 4 })} />
                            </label>
                            <label class={styles.checkField}>
                                <input type="checkbox" checked={form().compress} onChange={e => patchHarvest({ compress: e.currentTarget.checked })} />
                                <span><Text val="CROP_FARMER_F_COMPRESS"/></span>
                            </label>
                            <label class={styles.checkField}>
                                <input type="checkbox" checked={form().isEnabled} onChange={e => patchHarvest({ isEnabled: e.currentTarget.checked })} />
                                <span><Text val="CROP_FARMER_F_ENABLED"/></span>
                            </label>
                            <div class={styles.formActions}>
                                <button type="button" class="btn btn-secondary" onClick={() => setHarvestForm(null)} disabled={saving()}><Text val="CANCEL"/></button>
                                <button type="submit" class="btn btn-primary" disabled={saving() || !form().unloadStorageId}>
                                    <i class={saving() ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-floppy-disk'} /> <Text val="SAVE"/>
                                </button>
                            </div>
                        </form>
                    )}
                </Show>
            </Modal>

            <Modal open={plantForm() !== null} onClose={() => setPlantForm(null)} busy={saving()}
                title={plantForm()?.id ? <Text val="CROP_FARMER_EDIT_PLANT"/> : <Text val="CROP_FARMER_ADD_PLANT"/>}>
                <Show when={plantForm()}>
                    {(form) => (
                        <form class={styles.form} onSubmit={savePlant}>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="CROP_FARMER_F_NAME"/></span>
                                <input class={styles.input} value={form().name} onInput={e => patchPlant({ name: e.currentTarget.value })} placeholder={t('CROP_FARMER_DEFAULT_PLANT_NAME')} />
                            </label>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="CROP_FARMER_F_SOURCE_STORAGE"/></span>
                                <StorageSelect storages={storages()} value={form().sourceStorageId} onChange={sourceStorageId => patchPlant({ sourceStorageId })} placeholder={t('CROP_FARMER_SELECT_STORAGE_OPTION')} />
                            </label>

                            <div class={styles.coordGroup}>
                                <span class={styles.coordGroupLabel}><Text val="CROP_FARMER_F_FARMLAND_MIX"/></span>
                                <For each={FARMLAND_CROPS}>
                                    {(crop) => (
                                        <label class={styles.mixRow}>
                                            <span class={styles.mixLabel}>{cropIcon(crop.item)} <Text val={crop.label}/></span>
                                            <input class={`${styles.input} ${styles.percentInput}`} type="number" min="0" max="100" value={form()[crop.percent]}
                                                onInput={e => patchPlant({ [crop.percent]: Math.max(0, Math.min(100, parseInt(e.currentTarget.value) || 0)) })} />
                                            <span>%</span>
                                        </label>
                                    )}
                                </For>
                                <span class={mixTotal() === 100 ? styles.mixTotalOk : styles.mixTotalBad}>
                                    {t('CROP_FARMER_MIX_TOTAL', String(mixTotal()))}
                                </span>
                                <span class={styles.fieldHint}><Text val="CROP_FARMER_MIX_HINT"/></span>
                            </div>

                            <label class={styles.checkField}>
                                <input type="checkbox" checked={form().plantNetherWart} onChange={e => patchPlant({ plantNetherWart: e.currentTarget.checked })} />
                                <span><Text val="CROP_FARMER_F_NETHER_WART"/></span>
                            </label>
                            <label class={styles.checkField}>
                                <input type="checkbox" checked={form().isEnabled} onChange={e => patchPlant({ isEnabled: e.currentTarget.checked })} />
                                <span><Text val="CROP_FARMER_F_ENABLED"/></span>
                            </label>
                            <div class={styles.formActions}>
                                <button type="button" class="btn btn-secondary" onClick={() => setPlantForm(null)} disabled={saving()}><Text val="CANCEL"/></button>
                                <button type="submit" class="btn btn-primary" disabled={saving() || !form().sourceStorageId || mixTotal() !== 100}>
                                    <i class={saving() ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-floppy-disk'} /> <Text val="SAVE"/>
                                </button>
                            </div>
                        </form>
                    )}
                </Show>
            </Modal>
        </div>
    );
}
